// This file handles uploading data to the backend or custom factories

import { createUploaderComponent, useQuasar } from 'quasar'
import { resizeImage } from 'src/helpers/images'
import { pluraliseString } from 'src/helpers/globals'
import { store } from 'src/store'
import { computed, ref } from 'vue'
import { post } from 'src/axios/post'

export default createUploaderComponent({
  name: 'BackendUploader',
  props: {
    multiple: {
      type: Boolean,
      default: true
    },
    accept: {
      type: String
    },
    autoUpload: {
      type: Boolean,
      default: true
    },
    url: {
      type: String,
      default: 'v1/files/upload'
    },
    category: {
      type: String,
      default: null
    },
    factory: {
      type: Function,
      default: null
    },
    capture: {
      type: String
    },
    disable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['start', 'finish', 'uploaded', 'failed', 'added'],
  injectPlugin ({ props, emit, helpers }) {
    const $q = useQuasar()

    const files = ref([])
    const uploadSize = ref(0)
    const uploadedSize = ref(0)
    const loading = ref(false)
    const checkingImages = ref(false)
    const checkingProgress = ref(0)

    // [ REQUIRED! ]
    // We're working on uploading files
    const isUploading = computed(() => {
      return loading.value
    })

    const canUpload = computed(() => {
      return !props.disable && !isUploading.value && !checkingImages.value
    })

    // [ optional ]
    // Shows overlay on top of the
    // uploader signaling it's waiting
    // on something (blocks all controls)
    const isBusy = computed(() => {
      return props.loading || checkingImages.value
    })

    function reset () {
      uploadSize.value = 0
      uploadedSize.value = 0
      checkingImages.value = false
      checkingProgress.value = 0
      files.value = []
    }

    function onUploadProgress (ev) {
      uploadSize.value = ev.total
      uploadedSize.value = ev.loaded
    }

    // [ REQUIRED! ]
    // Abort and clean up any process
    // that is in progress
    function abort () {
      reset()
    }

    /**
     * Process files for upload. Try shrinking if images
     * @returns {Promise<FormData>}
     */
    async function processFilesBeforeUpload () {
      const formData = new FormData()
      if (props.category) {
        formData.append('category', props.category)
      }

      checkingImages.value = true
      checkingProgress.value = 0
      for (const key in files.value) {
        const file = files.value[key]
        try {
          const resizedImage = await resizeImage({
            file
          })
          const tempImageName = 'CapturedImage-' + Date.now() + '.jpg'
          if (props.multiple) {
            formData.append('files[]', await resizedImage, (file.name || tempImageName))
          }
          else {
            formData.append('file', await resizedImage, (file.name || tempImageName))
          }
        }
        catch (err) {
          // if file is a data uri blob, continue
          if (typeof file === 'string') {
            continue
          }
          // Not an image. proceed
          if (props.multiple) {
            formData.append('files[]', file)
          }
          else {
            formData.append('file', file)
          }
        }
        finally {
          checkingProgress.value = key / files.value.length
        }
      }
      checkingImages.value = false
      return formData
    }

    // [ REQUIRED! ]
    // Start the uploading process
    async function upload () {
      if (!canUpload.value) {
        return
      }

      if (props.factory) {
        props.factory(files.value)
        return
      }

      const fileLimit = store.getters.getSystemSetting('upload_limit') || 0
      if (fileLimit > 0 && files.value.length > fileLimit) {
        $q.dialog({
          title: 'Error',
          message: [
            {
              labelClass: 'text-bold',
              message: `You can only upload up to ${fileLimit} files per request. You attempted to upload ${files.value.length} files.`
            }
          ],
          ok: 'Close'
        })
          .onDismiss(() => {
            reset()
          })
        return
      }

      loading.value = true
      emit('start')
      const { status, payload, errors, message } = await post(props.url, await processFilesBeforeUpload(), { onUploadProgress })
      emit('finish')
      loading.value = false
      reset()
      if (status === 'success') {
        const successStr = `${payload.files.length} ${pluraliseString('file', payload.files.length)} successfully uploaded`

        if (payload.files.length > 0 && payload.failed.length < 1) {
          $q.notify({
            color: 'green',
            message: successStr
          })
        }

        if (payload.failed.length > 0) {
          const arr = []
          arr.push({
            labelClass: 'text-bold text-green-7',
            message: successStr
          })

          arr.push({
            labelClass: 'text-bold text-red-7',
            message: `${payload.failed.length} ${pluraliseString('file', payload.failed.length)} failed to upload`
          })

          $q.dialog({
            title: 'Files failed to upload',
            message: [
              ...arr,
              ...payload.failed
            ],
            ok: 'Close'
          })
        }

        if (payload.files.length > 0) {
          setTimeout(() => {
            emit('uploaded', payload.files)
          }, 1000)
        }
      }
      else {
        emit('failed')

        $q.dialog({
          title: 'Error',
          message: [
            {
              labelClass: 'text-bold',
              message
            },
            ...Object.values(errors).map(item => `- ${item[0]}`)
          ],
          ok: 'Close'
        })
      }
    }

    return {
      files,
      uploadSize,
      uploadedSize,
      loading,
      checkingImages,
      checkingProgress,

      isUploading,
      isBusy,

      abort,
      upload
    }
  }
})
